import React, { useCallback, useState } from 'react';
import cn from 'classnames';
import styled from 'styled-components';
import { motion as m } from 'framer-motion';
import { captureMediaLoadError } from 'common/modules/media/captureMediaLoadError';
import { customMediaQuery } from 'students/views/shared/styled';
import { useHeartBeatMediaControl } from 'students/views/shared/components/HeartBeat';
import VideoPlayer from './VideoPlayer';
import CustomVideoPlayer from './CustomVideoPlayer';
import { isEmbedPlatformUrl } from './CustomVideoPlayer/utils/isEmbedPlatformUrl';
import { useCheckForResize, useStopOnLeave, useVideoResizeFallback } from './hooks';

interface IProps {
  videoURL: string;
  subtitlesURL?: string;
  accentColor?: string;
  className?: string;
  onResizeEnd?: () => void;
}

const VideoContent: React.FC<IProps> = ({
  videoURL,
  subtitlesURL,
  accentColor,
  className,
  onResizeEnd
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [mediaWrapperRef, checkForResize] = useCheckForResize();
  const { onStartPlaying, onStopPlaying } = useHeartBeatMediaControl();
  const useEmbedPlayer = isEmbedPlatformUrl(videoURL);
  const setVideoIsReady = useVideoResizeFallback(onResizeEnd);

  const stopPlaying = useCallback(() => {
    setIsPlaying(false);
  }, [setIsPlaying]);

  useStopOnLeave(stopPlaying);

  const handleReady = (): void => {
    setVideoIsReady(true);
    checkForResize();
    onResizeEnd && onResizeEnd();
  };

  const handlePlay = (): void => {
    setIsPlaying(true);
    onStartPlaying();
  };

  const handlePause = () => {
    onStopPlaying();
  };

  const handleEnded = () => {
    onStopPlaying();
  };

  const stopOnError = () => {
    onStopPlaying();
    setIsPlaying(false);
  };

  // No Sentry report here: the embed path reports itself, tagged `media_context: embed`
  // (VideoPlayer.tsx), and a second one only races that tag through the one-per-src dedup.
  const handleEmbedPlayerError = () => {
    stopOnError();
  };

  const handleCustomPlayerError = (error?: MediaError | null) => {
    captureMediaLoadError(error ?? new Error('Video failed to load'), {
      kind: 'video',
      src: videoURL,
      context: 'video-element'
    });
    stopOnError();
  };

  return (
    <SWrapper className={cn(className)} ref={mediaWrapperRef}>
      {/*
       * The fade-in is decorative only: visibility must never depend on a player event,
       * or a video that never reports ready hides the player, spinner and error UI too.
       */}
      <SVideoContainer
        className="ln-grow"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
      >
        {useEmbedPlayer ? (
          <VideoPlayer
            className="ln-grow"
            controls
            onPlay={handlePlay}
            onStart={handlePlay}
            onEnded={handleEnded}
            onPause={handlePause}
            onReady={handleReady}
            onError={handleEmbedPlayerError}
            onVideoPlayerError={handleEmbedPlayerError}
            config={{
              wistia: {
                options: {
                  videoFoam: true,
                  playerColor: '00a5d7'
                }
              },
              youtube: {
                embedOptions: {
                  host: 'https://www.youtube-nocookie.com'
                },
                playerVars: {
                  rel: 0,
                  modestbranding: 1,
                  disablekb: 1,
                  fs: 0,
                  iv_load_policy: 3,
                  playsinline: 1
                }
              }
            }}
            width="100%"
            height="100%"
            playing={isPlaying}
            loop={false}
            url={videoURL}
            volume={1}
          />
        ) : (
          <CustomVideoPlayer
            url={videoURL}
            subtitlesURL={subtitlesURL}
            accentColor={accentColor}
            playing={isPlaying}
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={handleEnded}
            onReady={handleReady}
            onError={handleCustomPlayerError}
          />
        )}
      </SVideoContainer>
    </SWrapper>
  );
};

export default VideoContent;

const SWrapper = styled.div`
  margin: 0 -1rem;
  position: relative;
  border-top-right-radius: 20px;
  border-top-left-radius: 20px;
  height: 300px;
  background: rgba(0, 0, 0, 0.3);
  transition: width 0.5s, height 0.5s;

  ${customMediaQuery('tablet')} {
    margin: 0;
    border-radius: 20px;
    overflow: hidden;
    height: 400px;
  }
`;

const SVideoContainer = styled(m.div)`
  width: 100%;
  height: 100%;
`;
